/**
 * Wizard Question Grouping Utilities
 * Phase 4 - Smart Grid with Wizard Grouping
 * 
 * Functions to organize questions by wizard groups
 */

import { projectId } from './supabase/info';

export interface Question {
  id: string;
  text: string;
  type: string;
  category: string;
  required: boolean;
  critical: boolean;
  grouping: {
    isPartOfWizard: boolean;
    wizardId?: string;
    wizardName?: string;
    pageNumber?: number;
    questionIndex?: number;
    gridBehavior: 'inline' | 'wizard_only' | 'both';
  };
}

export interface QuestionAnswer {
  questionId: string;
  questionText: string;
  answer: string;
  answeredAt?: string;
  updatedAt?: string;
  confidence?: string;
}

export interface WizardGroup {
  wizardId: string;
  wizardName: string;
  questions: Array<{
    questionId: string;
    questionText: string;
    answer?: string;
    answered: boolean;
    required: boolean;
    pageNumber?: number;
    gridBehavior: string;
  }>;
  sessionExists: boolean;
  sessionComplete: boolean;
  currentPage: number;
  totalPages: number;
}

export interface GroupedQuestions {
  wizardGroups: WizardGroup[];
  standaloneQuestions: QuestionAnswer[];
}

/**
 * Fetch question metadata from question bank
 */
export async function fetchQuestionMetadata(
  consultingFirmId: string,
  accessToken: string
): Promise<Record<string, Question>> {
  try {
    const response = await fetch(
      `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/question-bank/${consultingFirmId}/questions`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'x-consulting-firm-id': consultingFirmId
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch question metadata');
    }

    const data = await response.json();
    return data.questions || {};
  } catch (error) {
    console.error('Error fetching question metadata:', error);
    return {};
  }
}

/**
 * Fetch wizard session data for a config item
 */
export async function fetchWizardSessions(
  configItemId: string,
  companyId: string,
  accessToken: string
): Promise<Record<string, any>> {
  try {
    const response = await fetch(
      `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${companyId}/${configItemId}`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );

    if (!response.ok) {
      return {};
    }

    const data = await response.json();
    return data.configItem?.wizardSessions || {};
  } catch (error) {
    console.error('Error fetching wizard sessions:', error);
    return {};
  }
}

/**
 * Group questions by wizard affiliation
 */
export function groupQuestionsByWizard(
  answers: QuestionAnswer[],
  questionMetadata: Record<string, Question>,
  wizardSessions: Record<string, any>
): GroupedQuestions {
  const wizardGroupsMap = new Map<string, WizardGroup>();
  const standaloneQuestions: QuestionAnswer[] = [];

  // Process each answered question
  answers.forEach(qa => {
    const metadata = questionMetadata[qa.questionId];
    
    if (!metadata) {
      // No metadata found, treat as standalone
      standaloneQuestions.push(qa);
      return;
    }

    if (metadata.grouping.isPartOfWizard && metadata.grouping.wizardId) {
      // This question belongs to a wizard
      const wizardId = metadata.grouping.wizardId;
      const wizardName = metadata.grouping.wizardName || 'Wizard';

      if (!wizardGroupsMap.has(wizardId)) {
        // Get wizard session info
        const session = wizardSessions[wizardId] || {};
        
        // Calculate total pages from questions
        const wizardQuestions = answers.filter(a => {
          const m = questionMetadata[a.questionId];
          return m?.grouping?.wizardId === wizardId;
        });
        
        const maxPage = Math.max(
          ...wizardQuestions.map(wq => {
            const m = questionMetadata[wq.questionId];
            return m?.grouping?.pageNumber || 1;
          }),
          1
        );

        wizardGroupsMap.set(wizardId, {
          wizardId,
          wizardName,
          questions: [],
          sessionExists: session.started || false,
          sessionComplete: session.completed || false,
          currentPage: session.lastPage || 0,
          totalPages: maxPage
        });
      }

      const group = wizardGroupsMap.get(wizardId)!;
      group.questions.push({
        questionId: qa.questionId,
        questionText: qa.questionText,
        answer: qa.answer,
        answered: !!qa.answer,
        required: metadata.required,
        pageNumber: metadata.grouping.pageNumber,
        gridBehavior: metadata.grouping.gridBehavior
      });
    } else {
      // Standalone question
      standaloneQuestions.push(qa);
    }
  });

  // Sort wizard groups by wizard name
  const wizardGroups = Array.from(wizardGroupsMap.values()).sort((a, b) => 
    a.wizardName.localeCompare(b.wizardName)
  );

  // Sort questions within each wizard by page number, then question index
  wizardGroups.forEach(group => {
    group.questions.sort((a, b) => {
      if (a.pageNumber !== b.pageNumber) {
        return (a.pageNumber || 0) - (b.pageNumber || 0);
      }
      return 0; // Maintain original order within same page
    });
  });

  return {
    wizardGroups,
    standaloneQuestions
  };
}

/**
 * Check if a question should allow inline editing based on gridBehavior
 */
export function canEditInline(
  questionId: string,
  questionMetadata: Record<string, Question>
): boolean {
  const metadata = questionMetadata[questionId];
  
  if (!metadata) {
    return true; // Default to allowing inline edit if no metadata
  }

  if (!metadata.grouping.isPartOfWizard) {
    return true; // Standalone questions can always be edited inline
  }

  // Wizard questions depend on gridBehavior
  const behavior = metadata.grouping.gridBehavior;
  return behavior === 'inline' || behavior === 'both';
}

/**
 * Get all unanswered wizard questions for a config item
 */
export async function getUnansweredWizardQuestions(
  configItemId: string,
  companyId: string,
  consultingFirmId: string,
  accessToken: string
): Promise<Array<{ wizardId: string; wizardName: string; questionCount: number }>> {
  try {
    // Fetch config item to get all questions
    const ciResponse = await fetch(
      `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${companyId}/${configItemId}`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );

    if (!ciResponse.ok) {
      return [];
    }

    const ciData = await ciResponse.json();
    const allQuestions = ciData.configItem?.questions || [];
    const answers = ciData.configItem?.answers || [];

    // Get question metadata
    const questionMetadata = await fetchQuestionMetadata(consultingFirmId, accessToken);

    // Find wizard questions that are unanswered
    const wizardQuestionsMap = new Map<string, { wizardId: string; wizardName: string; count: number }>();

    allQuestions.forEach((q: any) => {
      const metadata = questionMetadata[q.id];
      
      if (metadata?.grouping?.isPartOfWizard && metadata.grouping.wizardId) {
        const wizardId = metadata.grouping.wizardId;
        const wizardName = metadata.grouping.wizardName || 'Wizard';
        
        // Check if this question is answered
        const isAnswered = answers.some((a: any) => a.questionId === q.id && a.answer);
        
        if (!isAnswered) {
          if (!wizardQuestionsMap.has(wizardId)) {
            wizardQuestionsMap.set(wizardId, {
              wizardId,
              wizardName,
              count: 0
            });
          }
          
          const entry = wizardQuestionsMap.get(wizardId)!;
          entry.count++;
        }
      }
    });

    return Array.from(wizardQuestionsMap.values());
  } catch (error) {
    console.error('Error getting unanswered wizard questions:', error);
    return [];
  }
}
